import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /**
   * Initializes layout listeners and card image sizes.
   */
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#observeGrid();
    this.#syncImageSizes();
    this.setAttribute('initialized', '');
  }

  /**
   * Synchronizes card image sizes after a section render.
   */
  updatedCallback() {
    super.updatedCallback();

    this.#observeGrid();
    this.#syncImageSizes();
  }

  /**
   * Removes layout listeners and stops observing the grid.
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#gridObserver.disconnect();
    this.#observedGrid = undefined;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
    this.#syncImageSizes();
  }

  /**
   * Synchronizes mobile image sizes with the current grid view.
   * Relies on snippets/card-gallery.liquid serving the mobile fallback `, 50vw` for small mobile cards
   * and `, 100vw` for large cards, which are skipped.
   */
  #syncImageSizes() {
    const { grid } = this.refs;
    if (!grid) return;
    if (grid.classList.contains('product-grid-mobile--large')) return;

    const isMobileSingle = grid.getAttribute('product-grid-view') === 'mobile-single';

    for (const image of grid.querySelectorAll('.card-gallery img[sizes]')) {
      const sizes = image.getAttribute('sizes');

      if (isMobileSingle && sizes?.endsWith(', 50vw')) {
        image.setAttribute('sizes', sizes.replace(/, 50vw$/, ', 100vw'));
      } else if (!isMobileSingle && sizes?.endsWith(', 100vw')) {
        image.setAttribute('sizes', sizes.replace(/, 100vw$/, ', 50vw'));
      }
    }
  }

  /**
   * Observes direct card insertions, including after the grid is replaced.
   */
  #observeGrid() {
    const { grid } = this.refs;
    if (grid === this.#observedGrid) return;

    this.#gridObserver.disconnect();
    this.#observedGrid = grid;

    if (grid) this.#gridObserver.observe(grid, { childList: true });
  }

  /** @type {HTMLUListElement | undefined} */
  #observedGrid;

  /**
   * Synchronizes image sizes when cards are appended or prepended.
   *
   * @param {MutationRecord[]} mutations
   */
  #handleGridMutations = (mutations) => {
    if (mutations.some((mutation) => mutation.addedNodes.length > 0)) {
      this.#syncImageSizes();
    }
  };

  /** @type {MutationObserver} */
  #gridObserver = new MutationObserver(this.#handleGridMutations);

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
